import { fontDisplay, fontText, hexToRgbA, StylesIndexInjections, isMobileApp } from 'common-react';
import { fieldBoxShadow, focusStyle } from '../styles/mixins';
import { createStyles, makeUiStyles, Theme } from '../styles/styles';
import { DatePickerSize } from './tools/DatePicker.const';
import { DatePickerStylePropsModel } from './tools/DatePicker.model';

const mobileBottomPadding = 16;
const getMobileBottomPadding = (theme: Theme) =>
  theme.space.mobileBottomPadding() + mobileBottomPadding;

const datePickerSize = (size: DatePickerSize) => {
  if (size === DatePickerSize.SMALL) {
    return {
      padding: '8px 16px',
      height: 40,
    }
  }
  return {
    padding: 16,
    height: 56,
  }
}

export default makeUiStyles((theme: Theme) => createStyles({
  root: {
    paddingBottom: 0,
    position: 'relative',
    '& .MuiInputLabel-root': {
      marginBottom: 8,
    },
    '& .MuiInputBase-root': {
      padding: (props: DatePickerStylePropsModel) => datePickerSize(props.sizes).padding,
      height: (props: DatePickerStylePropsModel) => datePickerSize(props.sizes).height,
      '& .MuiInputBase-input': {
        padding: '12px 0',
        marginRight: 12,
      },
      '&.Mui-focusVisible': {
        '& .MuiInputBase-input': {
          ...focusStyle(theme),
          borderColor: theme.color.greys[40]
        }
      },
      '&.Mui-error': {
        borderColor: theme.color.reds[100],
      },
      '&:hover, &.Mui-focused:not(.Mui-focusVisible)': {
        boxShadow: fieldBoxShadow(theme).bs1,
        borderColor: theme.color.greys[80],
        caretColor: theme.color.oranges[50],
      },
      '&.Mui-disabled': {
        boxShadow: 'none',
        border: `1px solid ${hexToRgbA(theme.color.greys[40], 0.4)}`,
        outline: 'none',
        color: hexToRgbA(theme.color.greys[40], 0.4),
      },
    },
  },
  datePickerPopover: {
    '&$datePickerPopoverWithToolbar': {
      '& .MuiPickersBasePicker-pickerView': {
        display: 'none',
      },
    },
    '& .MuiIconButton-root': {
      color: theme.color.greys[80],
      backgroundColor: 'transparent',
      padding: '12px 8px'
    },
    '& .MuiPickersCalendar-week': {
      marginBottom: 2,
      [theme.breakpoints.down('sm')]: {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
      }
    },
    '& .MuiPaper-root': {
      marginTop: 8,
      border: `1px solid ${theme.color.greys[15]}`,
      borderRadius: 4,
      boxShadow: fieldBoxShadow(theme).bs3,
      backgroundColor: theme.color.greys[0],
      [theme.breakpoints.between('xs', 'sm')]: {
        margin: 'auto',
      },
      [theme.breakpoints.down('sm')]: {
        margin: 'auto 0 0',
        width: '100%',
        borderRadius: 0,
        top: 0,
        '& .MuiDialogActions-root': {
          display: 'flex',
          gap: 16,
          flexDirection: 'column-reverse',
          justifyContent: 'center',
          paddingBottom: isMobileApp() ? getMobileBottomPadding(theme) : mobileBottomPadding,
          paddingTop: 0,
          '& .MuiButton-root': {
            color: theme.color.greys[0],
            backgroundColor: theme.color.greys[80],
            borderRadius: 80,
            width: '100%',
            font: fontDisplay('normal', theme.fontSize.l, 28),
            padding: '8px 16px',
            textTransform: 'none',
          },
          '& .MuiButton-root:first-child': {
            backgroundColor: theme.color.greys[0],
            color: theme.color.greys[80],
          }
        }
      }
    },
    '& .MuiPickersCalendarHeader-daysHeader': {
      maxHeight: 'none',
      [theme.breakpoints.down('sm')]: {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
      }
    },
    '& .MuiPickersBasePicker-pickerView': {
      minWidth: 'auto',
      minHeight: 'auto',
      padding: '0 4px',
      maxWidth: 498,
      [theme.breakpoints.down('sm')]: {
        padding: '0 16px',
        maxWidth: '100%',
      }
    },
    '& .MuiPickersDay-day': {
      width: 70,
      padding: '0 8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: 40,
      margin: 0,
      borderRadius: 2,
      border: '1px solid transparent',
      backgroundColor: theme.color.greys[10],
      [theme.breakpoints.down('sm')]: {
        height: 36,
        width: 'auto',
        padding: 0,
        margin: '0 auto',
      },
      '& .MuiIconButton-label': {
        width: 40,
        height: 40,
        borderRadius: 4,
      },
      '&:hover': {
        backgroundColor: theme.color.greys[20],
        [theme.breakpoints.down('sm')]: {
          backgroundColor: theme.color.greys[10],
        },
      },
      '& .MuiTypography-root': {
        font: fontText('normal', theme.fontSize.base, 24),
        color: theme.color.greys[80],
        textTransform: 'uppercase',
      },
      '&Selected': {
        '& .MuiIconButton-label': {
          backgroundColor: theme.color.greys[80],
          color: theme.color.greys[0],
          borderRadius: 4,
          margin: 0,
          width: 40,
          height: 40,
        },
        '&:hover': {
          backgroundColor: theme.color.greys[10],
        },

        '& .MuiTypography-root': {
          color: theme.color.greys[0],
          font: fontDisplay('normal', theme.fontSize.base, 24),
        }
      },
      '&.MuiPickersDay-hidden': {
        opacity: 1,
        backgroundColor: theme.color.greys[0],
        '& .MuiIconButton-label': {
          backgroundColor: 'transparent',
        },
        '& .MuiTypography-root': {
          color: theme.color.greys[40],
          font: fontText('normal', theme.fontSize.base, 24),
        }
      },
      '&.MuiPickersDay-current': {
        '& .MuiIconButton-label': {
          borderRadius: '50%',
          border: `2px solid ${theme.color.greys[80]}`,
          '& .MuiTypography-root': {
            font: fontDisplay('normal', theme.fontSize.base, 24),
          }
        },
        '&:hover': {
          backgroundColor: theme.color.greys[10],
          '& .MuiIconButton-label': {
            backgroundColor: theme.color.greys[80],
            '& .MuiTypography-root': {
              color: theme.color.greys[0],
            }
          }
        },
      },
      '&Disabled': {
          backgroundColor: theme.color.greys[10],
          color: theme.color.greys[80],
          opacity: 0.4,
          pointerEvents: 'none',
      },
    },
    '& .MuiPickersCalendarHeader-switchHeader': {
      marginBottom: 16,
      justifyContent: 'flex-start',
      padding: '8px 16px 0px 20px',
      [theme.breakpoints.down('sm')]: {
        padding: '8px 16px 0px 12px',
      }
    },
    '& .MuiPickersCalendar-transitionContainer': {
      marginTop: 5,
      minHeight: 254,
    },
    '& .MuiPickersCalendarHeader-transitionContainer': {
      height: 34,
      width: 172,
      '& .MuiTypography-root': {
        font: fontDisplay('normal', theme.fontSize.l, 28),
        color: theme.color.greys[80],
        textTransform: 'capitalize',
        paddingTop: 4,
      },
    },
    '& .MuiPickersCalendarHeader-dayLabel': {
      width: 70,
      padding: 8,
      margin: 0,
      font: fontDisplay('normal', theme.fontSize.base, 24),
      color: theme.color.greys[40],
      textTransform: 'capitalize',
      [theme.breakpoints.down('sm')]: {
        padding: '8px 0',
        width: 'auto',
      }
    },
    '& .MuiPickersYearSelection-container': {
      padding: 16,

      '& .MuiPickersYear-root': {
        margin: 0,
        font: fontText('normal', theme.fontSize.base, 20),
        color: theme.color.greys[80],
        '&:hover': {
          color: theme.color.violets[50]
        },

        '&.MuiPickersYear-yearSelected': {
          color: theme.color.violets[50],
          '&:hover': {
            color: theme.color.violets[50]
          },
        }
      }
    },
    '& .MuiPickersMonthSelection-container': {
      [theme.breakpoints.down('xs')]: {
        width: 280
      },
      '& .MuiPickersMonth-root': {
        font: fontText('normal', theme.fontSize.base, 20),
        color: theme.color.greys[80],
        '&:hover': {
          color: theme.color.violets[50]
        },

        '&.MuiPickersMonth-monthDisabled': {
          color: theme.color.greys[40]
        },

        '&.MuiPickersMonth-monthSelected': {
          color: theme.color.violets[50],
          '&:hover': {
            color: theme.color.violets[50]
          },
        }
      }
    }
  },
  inputDatePicker: {
    color: theme.color.greys[80],
    font: fontText('normal', theme.fontSize.base, 24),
    border: `1px solid ${theme.color.greys[40]}`,
    borderRadius: 8,
    '& .MuiInputAdornment-positionEnd': {
      marginLeft: 0
    },
    '& > input::placeholder': {
      letterSpacing: -1,
      opacity: 1,
      color: theme.color.greys[40],
    }
  },
  calendarButton: {
    padding: 0,
    position: 'relative',
    top: 0,
    right: 0,
    borderRadius: 'unset',
    color: theme.color.oranges[50],
    '&.Mui-disabled': {
      color: hexToRgbA(theme.color.oranges[50], 0.4),
    },
    '&:focus-visible': {
      ...focusStyle(theme),
    },
    '&:hover': {
      backgroundColor: 'transparent',
    }
  },
  datePickerPopoverWithToolbar: {},
  toolbarWrapper: {
    padding: 2,
  },
  toolbarYearRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: 16,
  },
  monthButton: {
    padding: '16px 32px',
    cursor: 'pointer',
    border: 0,
    backgroundColor: theme.color.greys[10],
    font: fontText('normal', theme.fontSize.base, 24),
    color: theme.color.greys[80],
    transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    '&:hover': {
      backgroundColor: theme.color.greys[20],
    },
    [theme.breakpoints.down('sm')]: {
      padding: '16px',
    },
  },
  monthButtonSelected: {
    backgroundColor: theme.color.greys[80],
    color: theme.color.greys[0],
    font: fontDisplay('normal', theme.fontSize.base, 24),
    borderRadius: 4,
    '&:hover': {
      backgroundColor: theme.color.greys[80],
    }
  },
  monthGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    rowGap: 2,
    [theme.breakpoints.down('sm')]: {
      padding: '0px 16px 24px',
    },
  },
}), { name: 'Datepicker', index: StylesIndexInjections.UI_MOLECULES });
